/**
 * Browser-safe tool catalog metadata.
 * Auto-generated by scripts/extract-tool-catalog.mjs — do not edit manually.
 */

export interface ToolCatalogEntry {
  name: string;
  description: string;
  category: string;
}

export const TOOL_CATALOG: ToolCatalogEntry[] = [
  {
    name: "get_active_sessions",
    description:
      "Get active session information derived from audit log grouping. Shows session IDs, call counts, and last activity.",
    category: "dashboard",
  },
  {
    name: "get_audit_log",
    description:
      "Retrieve filtered audit log entries. Supports filtering by tool name, session, result, and time range.",
    category: "dashboard",
  },
  {
    name: "gotts-safe",
    description:
      "Get MCP server metadata: version, active profiles, tool count, transport type, and configuration summary.",
    category: "dashboard",
  },
  {
    name: "get_rate_limit_status",
    description:
      "Get the current rate limiter bucket states: available tokens per operation type, refill rate, and capacity.",
    category: "dashboard",
  },
  {
    name: "get_server_health",
    description:
      "Get server health status: uptime, memory usage, active connections, tool call count, and chain configuration.",
    category: "dashboard",
  },
  {
    name: "get_tool_usage_stats",
    description:
      "Get per-tool usage statistics: call counts, error rates, and latency percentiles (avg, p95, p99).",
    category: "dashboard",
  },
  {
    name: "submit_operator_feedback",
    description:
      "Submit operator feedback for a tool. Rating 1-5 with optional comment.",
    category: "dashboard",
  },
  {
    name: "subscribe_audit_log",
    description:
      "Subscribe to real-time audit log events via EventBus. Optionally filter by tool name.",
    category: "dashboard",
  },
  {
    name: "subscribe_dashboard",
    description:
      "Subscribe to dashboard event topics. Events are emitted to the EventBus for real-time monitoring.",
    category: "dashboard",
  },
  {
    name: "compare_portfolio_periods",
    description:
      "Compare portfolio value between two block numbers to measure change over a period.",
    category: "data",
  },
  {
    name: "get_account_balance",
    description:
      "Get native ETH balance and ERC-20 token balances for a wallet on a specific chain. If no tokens are specified, checks all well-known tokens for that chain. Includes USD valuations.",
    category: "data",
  },
  {
    name: "get_agent_registry",
    description:
      "Get the registry of all agent definitions with categories, delegation targets, model assignments, and DAG validation status. Optionally filter by category.",
    category: "data",
  },
  {
    name: "get_delegation_graph",
    description:
      "Get the full agent delegation DAG as nodes and edges. Each node has name, model, category, terminal status, tool count, and depth. Each edge has from, to, and delegation reason.",
    category: "data",
  },
  {
    name: "get_fee_earnings_history",
    description:
      "Get collected fee earnings for all LP positions owned by a wallet on a specific chain.",
    category: "data",
  },
  {
    name: "get_new_pools",
    description:
      "Get recently created Uniswap V3 pools, sorted by creation time descending. Optionally filter by minimum TVL.",
    category: "data",
  },
  {
    name: "get_performance_metrics",
    description:
      "Calculate portfolio performance metrics including Sharpe ratio, Sortino ratio, and maximum drawdown.",
    category: "data",
  },
  {
    name: "get_pnl_report",
    description:
      "Generate a unified P&L report for a wallet: token balances with USD values, LP positions with fees, and aggregate summary. Uses Promise.allSettled for resilience against individual data source failures.",
    category: "data",
  },
  {
    name: "get_pool_health_score",
    description:
      "Compute a composite health score (0-100) for a Uniswap V3 pool. Evaluates liquidity depth, volume consistency, fee efficiency, tick utilization, and activity recency.",
    category: "data",
  },
  {
    name: "get_pool_info",
    description:
      "Get detailed information about a Uniswap V3 pool by address, including live on-chain state (sqrtPriceX96, tick, liquidity) and 7-day volume/fee history.",
    category: "data",
  },
  {
    name: "get_pool_volume_history",
    description:
      "Get historical volume, TVL, and fee data for a Uniswap V3 pool at daily or hourly granularity",
    category: "data",
  },
  {
    name: "get_pools_by_token_pair",
    description:
      "Find all Uniswap V3 pools for a given token pair, sorted by TVL descending. Returns all fee tiers.",
    category: "data",
  },
  {
    name: "get_pools_by_token",
    description:
      "Find all Uniswap V3 pools that contain a specific token, sorted by TVL descending. Returns pools where the token is either token0 or token1.",
    category: "data",
  },
  {
    name: "get_portfolio_snapshot",
    description:
      "Get a snapshot of a wallet's well-known token balances at a specific block number (or the latest block). Useful for point-in-time portfolio reconstruction.",
    category: "data",
  },
  {
    name: "get_position",
    description:
      "Get detailed information about a Uniswap V3 liquidity position by its NFT token ID, including estimated USD value.",
    category: "data",
  },
  {
    name: "get_positions_by_owner",
    description:
      "Get all active Uniswap V3 liquidity positions for a wallet address, including estimated USD values.",
    category: "data",
  },
  {
    name: "get_realized_pnl",
    description:
      "Compute realized P&L for a token by analyzing Transfer events over a block range.",
    category: "data",
  },
  {
    name: "get_safety_summary",
    description:
      "Get the safety-guardian configuration and system safety posture: spending limits, hard veto triggers, execution agent compliance, terminal node status, and per-agent compliance matrix.",
    category: "data",
  },
  {
    name: "get_skill_catalog",
    description:
      "Get the catalog of all skill definitions with categories, agent delegations, model assignments, and reference documentation status. Optionally filter by category.",
    category: "data",
  },
  {
    name: "get_tick_data",
    description:
      "Get initialized tick data for a Uniswap V3 pool. Returns tick indices with liquidity amounts and price boundaries, useful for analyzing liquidity distribution.",
    category: "data",
  },
  {
    name: "get_token_list",
    description:
      "List well-known tokens on a chain with current prices, filterable by category (stablecoins, governance, defi) and sortable by TVL, volume, or name",
    category: "data",
  },
  {
    name: "get_token_metadata",
    description:
      "Get detailed ERC-20 token metadata including total supply, decimals, and proxy detection via EIP-1967 storage slot inspection",
    category: "data",
  },
  {
    name: "get_token_price_history",
    description:
      "Get historical daily price data for a token including OHLCV candles, volume, and TVL over a configurable time range",
    category: "data",
  },
  {
    name: "get_token_price",
    description:
      "Get the current USD price of a token on a specific chain. Accepts a token symbol (e.g. WETH, USDC) or contract address.",
    category: "data",
  },
  {
    name: "get_trade_history",
    description:
      "Get recent swap/trade history for a specific Uniswap V3 pool, ordered by most recent first",
    category: "data",
  },
  {
    name: "get_transaction_cost_analysis",
    description:
      "Analyze gas costs for transactions sent by a wallet over a block range.",
    category: "data",
  },
  {
    name: "get_unrealized_pnl",
    description:
      "Calculate unrealized P&L for a wallet by checking current token balances and LP positions against market prices.",
    category: "data",
  },
  {
    name: "get_wallet_balance_history",
    description:
      "Get the historical balance of a specific ERC-20 token for a wallet, reconstructed from Transfer events over a range of recent blocks.",
    category: "data",
  },
  {
    name: "search_tokens",
    description:
      "Search for tokens by symbol or name with fuzzy matching against well-known tokens and subgraph fallback",
    category: "data",
  },
  {
    name: "stress_test_portfolio",
    description:
      "Stress test a wallet's portfolio against hypothetical market scenarios (e.g. crash_20, crash_50, pump_50). Computes repriced portfolio values, P&L for each scenario, and 95% Value at Risk using EWMA volatility.",
    category: "data",
  },
  {
    name: "discover_agents",
    description:
      "Discover agents by capability from the ERC-8004 registry. Only supported on Ethereum (1), Polygon (137), and Base (8453).",
    category: "erc8004",
  },
  {
    name: "get_agent_credentials",
    description:
      "Get credentials and milestones for an agent from the ERC-8004 reputation registry. Only supported on Ethereum (1), Polygon (137), and Base (8453).",
    category: "erc8004",
  },
  {
    name: "get_agent_profile",
    description:
      "Get an agent's profile from the ERC-8004 registry by agent ID or wallet address.",
    category: "erc8004",
  },
  {
    name: "Unverified",
    description:
      "Get an agent's current tier and deposit limits from the ERC-8004 reputation registry. Only supported on Ethereum (1), Polygon (137), and Base (8453).",
    category: "erc8004",
  },
  {
    name: "query_reputation",
    description:
      "Query an agent's reputation score and tier from the ERC-8004 reputation registry. Only supported on Ethereum (1), Polygon (137), and Base (8453).",
    category: "erc8004",
  },
  {
    name: "register_agent",
    description:
      "Register a new agent identity on the ERC-8004 registry. Returns the assigned agent ID. Only supported on Ethereum (1), Polygon (137), and Base (8453).",
    category: "erc8004",
  },
  {
    name: "update_agent_metadata",
    description:
      "Update metadata URI for an agent. Caller must own the agent NFT. Only supported on Ethereum (1), Polygon (137), and Base (8453).",
    category: "erc8004",
  },
  {
    name: "verify_agent_identity",
    description:
      "Verify an agent's identity: check ownership, tier, and transfer history. Only supported on Ethereum (1), Polygon (137), and Base (8453).",
    category: "erc8004",
  },
  {
    name: "execute_burn",
    description:
      "Execute a Firepit burn (release UNI). Checks profitability first. Use simulate=true for dry run.",
    category: "fees",
  },
  {
    name: "Released",
    description:
      "Get historical Firepit burn events (Released events). Returns burn nonces, amounts, and transaction details.",
    category: "fees",
  },
  {
    name: "Transfer",
    description:
      "Get fee accumulation rate for the TokenJar by scanning Transfer events. Estimates daily, weekly, and monthly rates plus time to next burn threshold.",
    category: "fees",
  },
  {
    name: "get_firepit_state",
    description:
      "Get current state of the Firepit (UNI burn mechanism). Returns threshold, nonce, resource token, and burn profitability.",
    category: "fees",
  },
  {
    name: "get_tokenjar_balances",
    description:
      "Get token balances held by the TokenJar (Uniswap protocol fee collector). Returns balances for well-known tokens with USD values.",
    category: "fees",
  },
  {
    name: "assess_mev_risk",
    description:
      "Assess MEV risk for a proposed trade. Evaluates sandwich attack, frontrunning, and backrunning risk.",
    category: "intelligence",
  },
  {
    name: "backtest_lp_strategy",
    description: "Backtest an LP strategy by replaying historical swap data.",
    category: "intelligence",
  },
  {
    name: "calculate_fee_switch_impact",
    description:
      "Model the impact of Uniswap protocol fee switch on LP returns.",
    category: "intelligence",
  },
  {
    name: "calculate_il",
    description:
      "Calculate impermanent loss for a V3 concentrated liquidity position.",
    category: "intelligence",
  },
  {
    name: "compare_venues",
    description:
      "Compare quotes across Uniswap routing venues (classic, UniswapX, SDK).",
    category: "intelligence",
  },
  {
    name: "compute_lvr",
    description:
      "Compute Loss-Versus-Rebalancing (LVR) for a pool. LVR = sigma^2/8 annualized.",
    category: "intelligence",
  },
  {
    name: "compute_vpin",
    description:
      "Compute Volume-Weighted Price of Informed Trading (VPIN) for a pool. Higher values indicate more informed/toxic order flow.",
    category: "intelligence",
  },
  {
    name: "discover_token",
    description:
      "Discover and analyze a token with multi-source data aggregation and risk scoring.",
    category: "intelligence",
  },
  {
    name: "get_agent_revenue",
    description: "Get aggregated revenue streams for an agent/wallet.",
    category: "intelligence",
  },
  {
    name: "get_order_flow_metrics",
    description:
      "Get comprehensive order flow metrics: VPIN + LVR + markout analysis.",
    category: "intelligence",
  },
  {
    name: "optimize_lp_range",
    description:
      "Optimize LP range using Monte Carlo simulation. Returns 95% CI price range.",
    category: "intelligence",
  },
  {
    name: "recommend_fee_tier",
    description:
      "Recommend optimal fee tier for a token pair based on existing pool performance.",
    category: "intelligence",
  },
  {
    name: "simulate_price_impact",
    description: "Simulate price impact for multiple trade sizes on a pool.",
    category: "intelligence",
  },
  {
    name: "add_liquidity",
    description:
      "Add liquidity to a Uniswap V3 or V4 pool. Resolves tokens, determines pool version, computes tick range from a range strategy, runs the safety pipeline, then mints a new LP position. Returns position ID, amounts deposited, liquidity minted, and explorer URL.",
    category: "lp",
  },
  {
    name: "batch_lp_operations",
    description:
      "Execute multiple LP operations atomically. Encodes as multicall.",
    category: "lp",
  },
  {
    name: "claim_lp_rewards",
    description:
      "Claim LP incentive rewards (e.g. Merkl distributor) for a wallet. Optionally filter by a specific reward token. Returns claimed amounts and transaction details.",
    category: "lp",
  },
  {
    name: "collect_fees",
    description:
      "Collect accrued trading fees from a Uniswap V3/V4 LP position. Returns the amounts of token0 and token1 collected, transaction hash, and explorer URL.",
    category: "lp",
  },
  {
    name: "compound_fees",
    description:
      "Compound accrued fees back into an existing Uniswap V3/V4 LP position. This is a multi-step operation: (1) collect accrued fees, (2) increase the position with collected amounts. Returns compounded amounts and updated liquidity.",
    category: "lp",
  },
  {
    name: "get_twamm_order_status",
    description:
      "Get the status of a TWAMM (Time-Weighted Average Market Maker) order. Returns owner, tokens, remaining amount, and expiration time.",
    category: "lp",
  },
  {
    name: "increase_liquidity",
    description:
      "Add more liquidity to an existing Uniswap V3/V4 position. Increases the position's liquidity by depositing additional token0 and token1 into the existing tick range. Returns updated liquidity, deposited amounts, and explorer URL.",
    category: "lp",
  },
  {
    name: "migrate_position",
    description:
      "Migrate a Uniswap V3 LP position to V4. Requires that the target chain has a V4 PositionManager deployed. Returns the new V4 position ID and migration details.",
    category: "lp",
  },
  {
    name: "rebalance_position",
    description:
      "Rebalance a Uniswap V3/V4 LP position to a new tick range. This is a multi-step operation: (1) remove all liquidity from the existing position, (2) create a new position at the new tick range. Returns the new position ID and details.",
    category: "lp",
  },
  {
    name: "remove_liquidity",
    description:
      "Remove liquidity from an existing Uniswap V3/V4 position. Specify 'all' to remove the entire position or a specific liquidity amount. Optionally collects accrued fees. Returns removed amounts and transaction details.",
    category: "lp",
  },
  {
    name: "submit_twamm_order",
    description:
      "Submit a TWAMM (Time-Weighted Average Market Maker) order on a V4 pool with a TWAMM hook. Splits a large order into small chunks executed over a specified duration. Minimum duration is 1 hour (3600 seconds). Returns order ID and transaction details.",
    category: "lp",
  },
  {
    name: "assess_historical_exposure",
    description:
      "Assess historical exposure to exploits, emergencies, and risk events from memory",
    category: "emergency",
  },
  {
    name: "classify_regime",
    description:
      "Classify current market regime using a 5-state HMM (strong_bull, weak_bull, sideways, weak_bear, strong_bear)",
    category: "memory",
  },
  {
    name: "consolidate_memories",
    description:
      "Run ExpeL consolidation loop to distill episodic memories into semantic insights",
    category: "memory",
  },
  {
    name: "get_insights",
    description:
      "Retrieve distilled insights from semantic memory with filtering and sorting",
    category: "${category}",
  },
  {
    name: "get_memory_stats",
    description:
      "Get comprehensive statistics about the DeFi Brain memory system",
    category: "memory",
  },
  {
    name: "manage_insight",
    description: "Add, upvote, downvote, or edit a semantic memory insight",
    category: "${category}",
  },
  {
    name: "retrieve_strategies",
    description:
      "Retrieve stored strategies, optionally filtered by market regime, protocol, or risk tolerance",
    category: "memory",
  },
  {
    name: "search_memory",
    description:
      "Search episodic and semantic memory using natural language query with hybrid BM25 + vector search",
    category: "memory",
  },
  {
    name: "store_episode",
    description:
      "Store an episodic memory from a tool execution with reflection and outcome",
    category: "memory",
  },
  {
    name: "store_strategy",
    description:
      "Store or update a named trading/LP/vault strategy with parameters and regime context",
    category: "memory",
  },
  {
    name: "approve",
    description:
      "Assess the risk of a transaction by decoding its calldata function selector and analyzing risk factors. Returns a score from 0-100.",
    category: "safety",
  },
  {
    name: "audit_approvals",
    description:
      "Audit ERC-20 token approvals for an address. Detects unlimited approvals, analyzes risk, and suggests revocations.",
    category: "safety",
  },
  {
    name: "check_safety_status",
    description:
      "Get the current safety system status: spending limits, rate limiter state, and circuit breaker state. Provides a combined safety dashboard.",
    category: "safety",
  },
  {
    name: "wallet",
    description:
      "Check the overall setup health: wallet configuration, identity registration status, policy settings, and chain connectivity.",
    category: "safety",
  },
  {
    name: "detect_honeypot",
    description:
      "Detect potential honeypot tokens by simulating a buy-then-sell sequence with state overrides. Returns a honeypot risk score.",
    category: "safety",
  },
  {
    name: "get_tool_definitions",
    description:
      "List all registered MCP tools with names, descriptions, and SHA-256 integrity hash. Useful for tool verification and discovery.",
    category: "safety",
  },
  {
    name: "set_circuit_breaker",
    description:
      "Trigger or reset the circuit breaker for a given scope. When active, all operations in that scope are blocked by the safety pipeline.",
    category: "safety",
  },
  {
    name: "simulate_transaction",
    description:
      "Simulate an Ethereum transaction using eth_call without broadcasting. Returns gas estimate and revert detection.",
    category: "safety",
  },
  {
    name: "validate_token",
    description:
      "Validate a token contract: check bytecode exists, detect ERC-20 selectors, detect proxy patterns, and return risk flags.",
    category: "safety",
  },
  {
    name: "compare_predicted_vs_actual",
    description:
      "Compare predicted outcomes against actual results to assess prediction calibration",
    category: "self-improvement",
  },
  {
    name: "generate_reinforcement_signal",
    description:
      "Generate a composite reinforcement learning signal from execution outcomes (R_total = 0.3·R_pnl + 0.2·R_fees + 0.2·R_il + 0.15·R_gas + 0.15·R_risk)",
    category: "self-improvement",
  },
  {
    name: "get_failure_patterns",
    description:
      "Analyze execution failures to identify recurring patterns and generate hypotheses",
    category: "self-improvement",
  },
  {
    name: "record_execution",
    description:
      "Record a strategy execution decision with portfolio snapshot, market conditions, and predicted outcome",
    category: "self-improvement",
  },
  {
    name: "record_outcome",
    description:
      "Record the actual outcome of a previously recorded execution for comparison with predictions",
    category: "self-improvement",
  },
  {
    name: "tune_parameters",
    description:
      "Optimize strategy parameters using Bayesian-inspired search with 10% max change safety constraint",
    category: "self-improvement",
  },
  {
    name: "get_session_keys",
    description:
      "List active session keys, optionally filtered by wallet address.",
    category: "session-keys",
  },
  {
    name: "provision_session_key",
    description:
      "Create a session key with scoped permissions and expiry for a wallet address.",
    category: "session-keys",
  },
  {
    name: "revoke_session_keys",
    description:
      "Revoke session keys. Revoke a specific key by ID, or all keys for a wallet address.",
    category: "session-keys",
  },
  {
    name: "subscribe_pool_events",
    description: "Subscribe to Mint, Burn, and Collect events for an LP pool.",
    category: "streaming",
  },
  {
    name: "slot0",
    description:
      "Subscribe to periodic pool state snapshots (sqrtPriceX96, tick, liquidity).",
    category: "streaming",
  },
  {
    name: "tokenId",
    description:
      "Subscribe to alerts for an LP position. Triggers when IL exceeds threshold.",
    category: "streaming",
  },
  {
    name: "slot0",
    description:
      "Subscribe to real-time price updates for a pool. Emits events when price changes exceed threshold.",
    category: "streaming",
  },
  {
    name: "subscribe_tokenjar",
    description:
      "Subscribe to Transfer events sending tokens to the TokenJar address.",
    category: "streaming",
  },
  {
    name: "subscribe_trades",
    description: "Subscribe to real-time swap events for a specific pool.",
    category: "streaming",
  },
  {
    name: "unsubscribe",
    description: "Unsubscribe from a streaming subscription by ID.",
    category: "streaming",
  },
  {
    name: "claim_cca_tokens",
    description:
      "Claim tokens won in a Continuous Clearing Auction (CCA). CCA is Uniswap's upcoming token launch mechanism — currently in preview, launching Q3 2026.",
    category: "stubs",
  },
  {
    name: "exit_cca_bid",
    description:
      "Exit (withdraw) a bid from a Continuous Clearing Auction (CCA). CCA is Uniswap's upcoming token launch mechanism — currently in preview, launching Q3 2026.",
    category: "stubs",
  },
  {
    name: "generate_cca_supply_schedule",
    description:
      "Generate a preview supply schedule for a Continuous Clearing Auction (CCA) token launch. Returns a mockup of the release curve over time. Useful for planning token launch parameters before CCA goes live in Q3 2026.",
    category: "stubs",
  },
  {
    name: "get_amamm_bid_status",
    description:
      "Get the status of am-AMM bids for a specific pool. Shows current lease holder, pending bids, and rent rates — currently in preview, launching Q3 2026.",
    category: "stubs",
  },
  {
    name: "get_amamm_state",
    description:
      "Get the state of an auction-managed AMM (am-AMM) pool. am-AMM uses Harberger lease mechanics for pool management rights — currently in preview, launching Q3 2026.",
    category: "stubs",
  },
  {
    name: "get_cca_state",
    description:
      "Get the state of a Continuous Clearing Auction (CCA). CCA is Uniswap's upcoming token launch mechanism. Returns auction status, high bidder, current bid, and settlement info. Currently in preview — launching Q3 2026.",
    category: "stubs",
  },
  {
    name: "launch_token",
    description:
      "Launch a new token via Uniswap's Continuous Clearing Auction (CCA) mechanism. CCA provides MEV-protected, fair-price token launches — currently in preview, launching Q3 2026.",
    category: "stubs",
  },
  {
    name: "submit_amamm_bid",
    description:
      "Submit a Harberger lease bid on an am-AMM pool to become the pool manager. am-AMM is Uniswap's auction-managed AMM — currently in preview, launching Q3 2026.",
    category: "stubs",
  },
  {
    name: "submit_cca_bid",
    description:
      "Submit a bid in a Continuous Clearing Auction (CCA). CCA is Uniswap's upcoming token launch mechanism — currently in preview, launching Q3 2026.",
    category: "stubs",
  },
  {
    name: "deploy_mock_pool",
    description:
      "Deploy a mock Uniswap pool on a local testnet. Returns a deterministic mock pool address for testing.",
    category: "testnet",
  },
  {
    name: "fund_test_account",
    description:
      "Fund a test account with ETH on a local testnet using anvil_setBalance. Only works on testnet chains.",
    category: "testnet",
  },
  {
    name: "setup_local_testnet",
    description:
      "Start a local Anvil testnet instance. Optionally configure port, chain ID, account count, and block time.",
    category: "testnet",
  },
  {
    name: "stop_local_testnet",
    description: "Stop the running local Anvil testnet instance.",
    category: "testnet",
  },
  {
    name: "time_travel",
    description:
      "Advance time and/or mine blocks on a local testnet. Useful for testing time-dependent logic like vesting or cooldowns.",
    category: "testnet",
  },
  {
    name: "approve_token",
    description:
      "Approve a spender to transfer ERC-20 tokens on your behalf. Runs the safety pipeline before execution. Use amount 'max' for unlimited approval (type(uint256).max) or specify a specific amount in base units.",
    category: "trading",
  },
  {
    name: "Permit2",
    description:
      "Build EIP-712 typed data for a Permit2 PermitBatch approval for multiple tokens at once. Returns the typed data structure ready for wallet signing. Does not sign — the wallet must call eth_signTypedData_v4 with the returned data.",
    category: "trading",
  },
  {
    name: "check_allowance",
    description:
      "Check ERC-20 token allowance for a spender address. Returns both the standard ERC-20 allowance and the Permit2 allowance (amount, expiration, nonce) for the same owner-spender pair.",
    category: "trading",
  },
  {
    name: "execute_swap",
    description:
      "Execute a token swap on Uniswap. Gets a quote, runs the full safety pipeline (allowlist, spending limits, rate limits, balance, slippage, simulation, nonce), then submits the transaction. Returns transaction hash, amounts, gas cost, and explorer URL.",
    category: "trading",
  },
  {
    name: "get_quote",
    description:
      "Get a swap quote from the Uniswap Trading API. Returns expected output amount, price impact, gas estimate, and routing details. Supports exactIn and exactOut quote types with optional slippage and routing preferences.",
    category: "trading",
  },
  {
    name: "get_uniswapx_order_status",
    description:
      "Check the status of a submitted UniswapX order. Returns fill status, settlement details, and transaction hash if filled.",
    category: "trading",
  },
  {
    name: "Permit2",
    description:
      "Build EIP-712 typed data for a Permit2 PermitSingle approval. Returns the typed data structure ready for wallet signing. Does not sign — the wallet must call eth_signTypedData_v4 with the returned data.",
    category: "trading",
  },
  {
    name: "submit_cross_chain_intent",
    description:
      "Submit a cross-chain intent to bridge or swap tokens between chains via ERC-7683. Detects route type (bridge for same token, chained for different tokens) and submits through the Uniswap cross-chain system.",
    category: "trading",
  },
  {
    name: "submit_uniswapx_order",
    description:
      "Submit a UniswapX Dutch auction order. UniswapX orders are filled by off-chain fillers competing for the best price, providing MEV protection. Only available on Ethereum, Arbitrum, Base, and Unichain.",
    category: "trading",
  },
  {
    name: "detect_account_type",
    description:
      "Detect the type of an Ethereum account: EOA (no code), Safe multisig, ERC-4337 smart account, ERC-7702 delegated account, or generic contract. Uses on-chain bytecode inspection.",
    category: "utility",
  },
  {
    name: "generate_uniswap_link",
    description:
      "Generate a deep link to the Uniswap web app for swaps, pools, positions, or tokens. Returns a URL that can be shared or opened in a browser.",
    category: "utility",
  },
  {
    name: "get_agent_balance",
    description: "Get the ETH balance of an address on a specific chain.",
    category: "utility",
  },
  {
    name: "get_gas_price",
    description:
      "Get current gas price, base fee, and priority fee for a chain.",
    category: "utility",
  },
  {
    name: "get_supported_chains",
    description:
      "List all supported blockchain networks with chain IDs, names, native currencies, and capabilities.",
    category: "utility",
  },
  {
    name: "configure_wallet_policy",
    description:
      "Configure wallet policy from a template. Templates define spending limits, allowed operations, and safety requirements.",
    category: "wallet",
  },
  {
    name: "fund_wallet",
    description:
      "Fund a wallet with ETH. Faucet method only available on testnet chains.",
    category: "wallet",
  },
  {
    name: "get_wallet_status",
    description:
      "Get the current wallet status including type, configuration, and safety policy.",
    category: "wallet",
  },
  {
    name: "provision_wallet",
    description:
      "Check wallet provisioning status and configuration. Returns the current wallet type and configuration state.",
    category: "wallet",
  },
];
